// src/features/brain/BrainPanel.tsx
// Phase 6.6 Bundle 3.2 - Brain panel container.
// Phase 6.7.3 - "View history" link in panel header navigates to /app/brain.
// Phase 6.7.4 - When feature is disabled, render BrainFeatureDisabledState
//               instead of BrainChat.
//
// Wraps BrainChat in a shadcn Sheet. The Sheet renders as a right-side
// slide-over (width 420px) on sm+ screens; on mobile (<640px) it falls
// back to full-width over the viewport via the sheet variant.
//
// Open/close is controlled by useBrainStore. The panel is rendered once
// at the AppLayout level so triggers anywhere (Sidebar, Header, anywhere)
// can call useBrainStore.open() to toggle visibility.
//
// Access control: useBrainFeatureEnabled checks ai_features +
// organization_ai_settings to determine if brain.query is enabled for
// the current org. When false, the chat is gated and a styled upsell
// state renders in its place.
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, History } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useBrainStore } from "@/stores/brainStore";
import { BrainChat } from "./BrainChat";
import { BrainFeatureDisabledState } from "./BrainFeatureDisabledState";
import { useBrainFeatureEnabled } from "./useBrainQueries";
export function BrainPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOpen = useBrainStore((s) => s.isOpen);
  const close = useBrainStore((s) => s.close);
  const startNewConversation = useBrainStore((s) => s.startNewConversation);
  const activeConversationId = useBrainStore((s) => s.activeConversationId);
  const { data: featureEnabled, isLoading: featureLoading } =
    useBrainFeatureEnabled();
  function handleOpenChange(open: boolean) {
    if (!open) close();
  }
  function handleViewHistory() {
    close();
    navigate("/app/brain");
  }
  // While the feature flag is loading, show the chat (which will show its
  // own loading state). Once loaded, gate based on the result.
  const isGated = !featureLoading && featureEnabled === false;
  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 gap-0 w-full sm:w-[420px]"
      >
        <SheetHeader className="px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center justify-between gap-2 pr-8">
            <div className="flex flex-col items-start gap-0.5 text-left">
              <SheetTitle className="text-base">
                {t("brain.title")}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {t("brain.subtitle")}
              </SheetDescription>
            </div>
            {!isGated && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleViewHistory}
                  className="text-xs h-8"
                  aria-label={t("brain.history")}
                >
                  <History className="h-3.5 w-3.5 sm:mr-1" />
                  <span className="hidden sm:inline">{t("brain.history")}</span>
                </Button>
                {activeConversationId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={startNewConversation}
                    className="text-xs h-8"
                  >
                    <Plus className="h-3.5 w-3.5 sm:mr-1" />
                    <span className="hidden sm:inline">{t("brain.newConversation")}</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-hidden">
          {isGated ? <BrainFeatureDisabledState /> : <BrainChat />}
        </div>
      </SheetContent>
    </Sheet>
  );
}
